import { collection, doc, getDoc, getDocs, limit, query, Timestamp, where } from "firebase/firestore";
import { db } from "./firebase";
import { orderTotal } from "./fee";
import { eventKey } from "./eventKey";
import { isEn, L } from "./i18n";
import { applyEn } from "./eventEn";
export interface EvView { id: string; name: string; description: string; venue: string; disciplines: string[];
  classes: { id: string; name: string; capacity: number; fee: number; surcharge: number; remaining: number; maxPerOrder?: number }[]; dates: { start: string; end: string }; entryPeriod: { open: string; close: string };
  cancelAllowed: boolean; imageUrl: string;
  // 選択式の質問の optionLabels は表示用（英語の画面で英語の選択肢がある場合）。送る値は options（日本語）のままである
  extraFields: { key: string; label: string; type: string; options?: string[]; optionLabels?: string[]; required: boolean }[]; status: string; paused: boolean; phase: "before" | "open" | "after";
  // 誓約書。英語の画面で英語版がある大会では英語版（lang: "en"）、無ければ日本語版である。jaOnly は英語の画面で日本語版しか無いとき true
  waiver: { text: string; version: string; lang?: "en"; jaOnly?: boolean } | null; paymentMethods: "card_bank" | "bank" | "card"; feeIncluded: boolean; contact: { name: string; email: string; url?: string } | null;
  // 観覧チケットか（kind が "ticket"）、キャンセル待ちを受け付けるか（無ければチケットで有効、大会エントリーで無効）
  ticket: boolean; waitlist: boolean;
  // 追加の画像（コース図・席種図など）。無ければ空の配列
  gallery: { url: string; caption: string }[];
  // 支払額の端数の切り上げの単位（無ければ切り上げない）
  roundUnit?: number; }
export async function loadEvent(id: string): Promise<EvView> {
  if (!id) throw new Error(L("大会が指定されていません", "No event was specified."));
  // 短い URL（/e/ST26）から来たときは大会コードで探す。公開中・受付終了の大会だけを読める（下書きは見つからない）
  let s;
  if (id.startsWith("code:")) {
    const q = await getDocs(query(collection(db, "events"), where("code", "==", id.slice(5)), where("status", "in", ["open", "closed", "finished"]), limit(1)));
    if (q.empty) throw new Error(L("大会が見つかりませんでした", "The event was not found."));
    s = q.docs[0]; id = s.id;
  } else {
    s = await getDoc(doc(db, "events", id));
    if (!s.exists()) throw new Error(L("大会が見つかりませんでした", "The event was not found."));
  }
  const e = s.data() as any; const now = new Date();
  const iso = (t: Timestamp) => t.toDate().toISOString();
  const open = e.entryPeriod.open.toDate(), close = e.entryPeriod.close.toDate();
  const phase = e.status !== "open" || now > close ? "after" : now < open ? "before" : "open";
  const view: EvView = { id, name: e.name, description: e.description ?? "", venue: e.venue, disciplines: e.disciplines ?? [], status: e.status, paused: !!e.paused, cancelAllowed: !!e.cancelAllowed, imageUrl: e.imageUrl ?? "", extraFields: e.extraFields ?? [],
    waiver: e.waiver ?? null, paymentMethods: e.paymentMethods === "bank" || e.paymentMethods === "card" ? e.paymentMethods : "card_bank", feeIncluded: e.feeMode === "included", contact: e.contact?.name ? e.contact : null,
    gallery: Array.isArray(e.gallery) ? e.gallery.map((g: any) => ({ url: String(g?.url ?? ""), caption: String(g?.caption ?? "") })) : [],
    ticket: e.kind === "ticket", ...([10, 50, 100].includes(e.roundUnit) ? { roundUnit: e.roundUnit } : {}), waitlist: e.waitlistEnabled ?? e.kind === "ticket",
    classes: e.classes.map((c: any) => ({ ...c, surcharge: orderTotal(c.fee, 1, e.feeMode === "included", e.paymentMethods === "bank" ? "bank" : "card_bank", e.roundUnit).surcharge, remaining: Math.max(0, c.capacity - (e.classCounts?.[c.id] ?? 0)) })),
    dates: { start: iso(e.dates.start), end: iso(e.dates.end) }, entryPeriod: { open: iso(e.entryPeriod.open), close: iso(e.entryPeriod.close) }, phase };
  return isEn() ? applyEn(view, e.en) : view;
}

export const qid = () => eventKey(location.search, location.pathname);
